import React, { ReactNode } from 'react';
import Link from 'next/link';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAtom } from 'jotai';
import { tokenInfoAtom } from '@/app/atoms/token';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  disableClose?: boolean;
  disableCloseButton?: boolean;
  comment?: string;
  setComment?: (comment: string) => void;
}

const BaseModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  disableClose = false,
  disableCloseButton = false,
  comment,
  setComment,
}) => {
  const [tokenInfo] = useAtom(tokenInfoAtom);
  const { symbol } = tokenInfo;
  const handleClose = () => {
    if (!disableCloseButton) {
      onClose();
      if (comment && setComment) {
        setComment('');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
      <div className="flex flex-col items-center relative bg-neutral-900 px-4 mobile:px-8 py-8 rounded shadow-md z-50 max-w-md w-full">
        {!disableClose && (
          <div
            className={`absolute top-1 right-1 sm:top-4 sm:right-4 flex items-center justify-center p-2 text-xs rounded-full ${
              disableCloseButton
                ? 'cursor-not-allowed text-neutral-400 bg-neutral-900 hover:bg-neutral-900'
                : 'cursor-pointer hover:bg-neutral-950'
            }`}
            onClick={handleClose}
          >
            <XMarkIcon className="w-5 h-5" />
          </div>
        )}
        <h2 className="uppercase text-lg text-center text-yellow">{title}</h2>
        {children}
        <button
          className={`flex justify-center py-2 px-4 mx-auto border border-neutral-900 bg-neutral-800 rounded-md text-sm max-h-[40px] mt-2 w-full ${
            disableClose
              ? 'cursor-not-allowed hover:bg-neutral-800'
              : 'cursor-pointer hover:bg-neutral-950'
          }`}
          onClick={handleClose}
          disabled={disableClose}
        >
          Close
        </button>
        <div className="flex mt-2 text-xs">
          <div className="min-w-full">
            * To participate in voting on a proposal, you must be a delegate before the
            proposal is created.{' '}
            <Link
              href={`https://www.coinbase.com/price/${symbol.toLowerCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex whitespace-nowrap text-link hover:text-hover"
            >
              Get involved.
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseModal;
